import { type ProductEntry } from '#app/routes/mocra+/_form+/components/products/schema.ts'
import {
	PRODUCT_CATEGORY_OPTIONS,
	PRODUCT_STATUS_OPTIONS,
	PROFESSIONAL_USE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

export type ProductCategoryValue =
	(typeof PRODUCT_CATEGORY_OPTIONS)[number]['value']
export type ProductStatusValue =
	(typeof PRODUCT_STATUS_OPTIONS)[number]['value']
export type ProductProfessionalUseValue =
	(typeof PROFESSIONAL_USE_OPTIONS)[number]['value']

export type ProductDraft = {
	name: string
	sku: string
	category: ProductCategoryValue
	categoryOther: string
	professionalUse: ProductProfessionalUseValue
	status: ProductStatusValue
}

export function createProductDraft(
	overrides: Partial<ProductDraft> = {},
): ProductDraft {
	return {
		name: '',
		sku: '',
		category: 'leave_on_skincare',
		categoryOther: '',
		professionalUse: 'consumer',
		status: 'listed',
		...overrides,
	}
}

export function sanitizeProductDraft(draft: ProductDraft): ProductDraft {
	const trimmedName = draft.name.trim()
	const trimmedSku = draft.sku.trim()
	const normalizedCategory = isProductCategoryValue(draft.category)
		? draft.category
		: 'other'
	const normalizedProfessionalUse = isProductProfessionalUseValue(
		draft.professionalUse,
	)
		? draft.professionalUse
		: 'consumer'
	const normalizedStatus = isProductStatusValue(draft.status)
		? draft.status
		: 'listed'

	return {
		name: trimmedName,
		sku: trimmedSku,
		category: normalizedCategory,
		categoryOther:
			normalizedCategory === 'other' ? draft.categoryOther.trim() : '',
		professionalUse: normalizedProfessionalUse,
		status: normalizedStatus,
	}
}

export function collectProductErrors(draft: ProductDraft): string[] {
	const sanitized = sanitizeProductDraft(draft)
	const errors: string[] = []
	if (sanitized.name.length === 0) {
		errors.push('Enter the product name.')
	}
	if (
		sanitized.category === 'other' &&
		sanitized.categoryOther.trim().length === 0
	) {
		errors.push('Describe the product category.')
	}
	return errors
}

export function productDraftToEntry(draft: ProductDraft): ProductEntry {
	const sanitized = sanitizeProductDraft(draft)
	return {
		name: sanitized.name,
		category: sanitized.category,
		professionalUse: sanitized.professionalUse,
		status: sanitized.status,
		...(sanitized.sku.length > 0 ? { sku: sanitized.sku } : {}),
		...(sanitized.category === 'other' && sanitized.categoryOther.length > 0
			? { categoryOther: sanitized.categoryOther }
			: {}),
	}
}

export function productEntryToDraft(entry: ProductEntry): ProductDraft {
	const category = isProductCategoryValue(entry.category)
		? entry.category
		: 'other'
	const professionalUse = isProductProfessionalUseValue(entry.professionalUse)
		? entry.professionalUse
		: 'consumer'
	const status = isProductStatusValue(entry.status) ? entry.status : 'listed'
	const categoryOther =
		category === 'other' && typeof entry.categoryOther === 'string'
			? entry.categoryOther
			: ''
	return sanitizeProductDraft({
		name: entry.name,
		sku: entry.sku ?? '',
		category,
		categoryOther,
		professionalUse,
		status,
	})
}

export function isProductCategoryValue(
	value: unknown,
): value is ProductCategoryValue {
	return PRODUCT_CATEGORY_OPTIONS.some((option) => option.value === value)
}

export function isProductStatusValue(
	value: unknown,
): value is ProductStatusValue {
	return PRODUCT_STATUS_OPTIONS.some((option) => option.value === value)
}

export function isProductProfessionalUseValue(
	value: unknown,
): value is ProductProfessionalUseValue {
	return PROFESSIONAL_USE_OPTIONS.some((option) => option.value === value)
}

export function resolveCategoryLabel(value: ProductCategoryValue): string {
	return (
		PRODUCT_CATEGORY_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function resolveProfessionalUseLabel(
	value: ProductProfessionalUseValue,
): string {
	return (
		PROFESSIONAL_USE_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function resolveStatusLabel(value: ProductStatusValue): string {
	return (
		PRODUCT_STATUS_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}
